import { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Trash2, Search, Users, X, Save } from "lucide-react";
import { toast } from "sonner";
import { VatModeSelect } from "../lib/vatMode";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", address: "", city: "", zip: "", ico: "", dic: "", notes: "", client_type: "osoba", default_vat_mode: "" };

export default function Clients() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/clients").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.name) { toast.error("Zadejte jméno klienta"); return; }
        try {
            if (editing.id) await api.put(`/clients/${editing.id}`, editing);
            else await api.post("/clients", editing);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch { toast.error("Uložení selhalo"); }
    };

    const del = async (id) => {
        if (!window.confirm("Smazat klienta?")) return;
        await api.delete(`/clients/${id}`);
        load();
    };

    const filtered = items.filter((c) => !q || (c.name + " " + c.contact_person + " " + c.city).toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="clients-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Číselník</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Klienti</h1>
                    <p className="text-sm text-slate-600 mt-2">Seznam vašich klientů — použijete je v zakázkách, rozpočtech a fakturách.</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-client-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nový klient
                </button>
            </div>

            <div className="bg-white border border-slate-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-200">
                    <Search className="w-4 h-4 text-slate-400 mr-2" />
                    <input placeholder="Hledat klienta…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search"
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <span className="text-xs text-slate-400">{filtered.length} / {items.length}</span>
                </div>

                {filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádní klienti</div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">Přidejte prvního klienta pro znovupoužití v dokumentech.</p>
                        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                            <Plus className="w-4 h-4" /> Přidat klienta
                        </button>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-6 py-3 font-semibold">Jméno / firma</th>
                                <th className="px-6 py-3 font-semibold">Kontakt</th>
                                <th className="px-6 py-3 font-semibold">Město</th>
                                <th className="px-6 py-3 font-semibold">IČO</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((c, i) => (
                                <tr key={c.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                                    <td className="px-6 py-3">
                                        <button onClick={() => setEditing(c)} className="text-left font-medium text-slate-900 hover:text-orange-600">{c.name}</button>
                                        {c.contact_person && <div className="text-xs text-slate-500">{c.contact_person}</div>}
                                    </td>
                                    <td className="px-6 py-3 text-slate-600">
                                        <div>{c.phone || "—"}</div>
                                        <div className="text-xs text-slate-500">{c.email || ""}</div>
                                    </td>
                                    <td className="px-6 py-3 text-slate-600">{c.city || "—"}</td>
                                    <td className="px-6 py-3 text-slate-600 font-mono text-xs">{c.ico || "—"}</td>
                                    <td className="px-6 py-3 text-right">
                                        <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white border-2 border-slate-900 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">{editing.id ? "Upravit klienta" : "Nový klient"}</h2>
                            <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            <div className="inline-flex border border-slate-300 bg-white">
                                {[["osoba", "Fyzická osoba"], ["firma", "Firma"]].map(([v, l]) => (
                                    <button key={v} onClick={() => setEditing({ ...editing, client_type: v })} data-testid={`client-type-${v}`} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${(editing.client_type || "osoba") === v ? "bg-slate-900 text-white" : "text-slate-600"}`}>{l}</button>
                                ))}
                            </div>
                            <Field label="Jméno / Firma *" v={editing.name} on={(e) => setEditing({ ...editing, name: e.target.value })} testid="client-name" />
                            <Field label="Kontaktní osoba" v={editing.contact_person} on={(e) => setEditing({ ...editing, contact_person: e.target.value })} />
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Email" type="email" v={editing.email} on={(e) => setEditing({ ...editing, email: e.target.value })} />
                                <Field label="Telefon" type="tel" v={editing.phone} on={(e) => setEditing({ ...editing, phone: e.target.value })} />
                            </div>
                            <Field label="Adresa" v={editing.address} on={(e) => setEditing({ ...editing, address: e.target.value })} />
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="PSČ" v={editing.zip} on={(e) => setEditing({ ...editing, zip: e.target.value })} />
                                <div className="col-span-2"><Field label="Město" v={editing.city} on={(e) => setEditing({ ...editing, city: e.target.value })} /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 items-end">
                                <div>
                                    <Field label="IČO" v={editing.ico} on={(e) => setEditing({ ...editing, ico: e.target.value })} testid="client-ico" />
                                </div>
                                <Field label="DIČ" v={editing.dic} on={(e) => setEditing({ ...editing, dic: e.target.value })} />
                            </div>
                            <button onClick={async () => {
                                const ico = (editing.ico || "").replace(/\D/g, "");
                                if (ico.length !== 8) { toast.error("Zadejte 8místné IČO"); return; }
                                try {
                                    const { data } = await api.get(`/clients/ares/${ico}`);
                                    setEditing({ ...editing, name: data.name || editing.name, ico: data.ico, dic: data.dic || editing.dic,
                                                 address: data.address || editing.address, city: data.city || editing.city, zip: data.zip || editing.zip });
                                    toast.success("Údaje načteny z ARES");
                                } catch (e) { toast.error(e.response?.data?.detail || "ARES lookup selhal"); }
                            }} data-testid="ares-lookup-button"
                                    className="w-full px-4 py-2 text-xs uppercase tracking-wider font-semibold border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm transition-colors">
                                ⚡ Načíst údaje z ARES podle IČO
                            </button>
                            <Field label="Poznámka" v={editing.notes} on={(e) => setEditing({ ...editing, notes: e.target.value })} />
                            <div>
                                <label className="label-eyebrow block mb-1.5">Výchozí režim DPH na fakturách</label>
                                <VatModeSelect value={editing.default_vat_mode || ""} onChange={(v) => setEditing({ ...editing, default_vat_mode: v })} allowAuto testid="client-default-vat-mode" />
                            </div>
                        </div>
                        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-end">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                            <button onClick={save} data-testid="save-client" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1">
                                <Save className="w-3.5 h-3.5" /> Uložit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, v, on, type = "text", testid }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v || ""} onChange={on} data-testid={testid}
                   className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm" />
        </div>
    );
}
